/**
 * Deal Intelligence Dashboard - API Client
 * Centralized API calls to backend
 */

import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance with defaults
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

// ============== DEALS ==============

export const dealsApi = {
  /**
   * Get all deals with optional filters
   */
  getAll: async (params = {}) => {
    const response = await api.get('/deals', { params });
    return response.data;
  },

  /**
   * Get deal summaries for pipeline view
   */
  getSummaries: async () => {
    const response = await api.get('/deals/summary');
    return response.data;
  },

  /**
   * Get pipeline statistics
   */
  getStats: async () => {
    const response = await api.get('/deals/stats');
    return response.data;
  },

  /**
   * Get single deal by ID
   */
  getById: async (dealId) => {
    const response = await api.get(`/deals/${dealId}`);
    return response.data;
  },

  /**
   * Create new deal
   */
  create: async (dealData) => {
    const response = await api.post('/deals', dealData);
    return response.data;
  },

  /**
   * Update existing deal
   */
  update: async (dealId, dealData) => {
    const response = await api.put(`/deals/${dealId}`, dealData);
    return response.data;
  },

  /**
   * Delete deal
   */
  delete: async (dealId) => {
    const response = await api.delete(`/deals/${dealId}`);
    return response.data;
  },

  /**
   * Recalculate deal metrics
   */
  calculate: async (dealId) => {
    const response = await api.post(`/deals/${dealId}/calculate`);
    return response.data;
  },
};

// ============== QUICK CALCULATOR ==============

export const calculatorApi = {
  /**
   * Quick deal analysis
   */
  quickAnalysis: async (params) => {
    const response = await api.post('/calculate/quick', null, { params });
    return response.data;
  },
};

// ============== DEAL ANALYZER ENGINE ==============

export const analyzerApi = {
  /**
   * Full deal analysis with scenarios, sensitivity, and risk assessment
   */
  analyzeDeal: async (inputData) => {
    const response = await api.post('/analyzer/analyze', inputData);
    return response.data;
  },

  /**
   * Analyze an existing deal from the database
   */
  analyzeExistingDeal: async (dealId, targetRoiPct = 20.0, marketAvgPricePerSqm = null) => {
    const params = { target_roi_pct: targetRoiPct };
    if (marketAvgPricePerSqm) params.market_avg_price_per_sqm = marketAvgPricePerSqm;
    const response = await api.post(`/analyzer/deal/${dealId}`, null, { params });
    return response.data;
  },

  /**
   * Calculate max bid price
   */
  calculateMaxBid: async (params) => {
    const response = await api.post('/analyzer/max-bid', null, { params });
    return response.data;
  },
};

// ============== RENOVATION CONTROL ==============

export const renovationApi = {
  /**
   * Get renovation tracking for a deal
   */
  getTracking: async (dealId) => {
    const response = await api.get(`/deals/${dealId}/renovation`);
    return response.data;
  },

  /**
   * Update renovation actuals
   */
  updateActuals: async (dealId, data) => {
    const params = {};
    if (data.actual_spend !== undefined) params.actual_spend = data.actual_spend;
    if (data.actual_start_date) params.actual_start_date = data.actual_start_date;
    if (data.actual_end_date) params.actual_end_date = data.actual_end_date;
    if (data.actual_duration_days) params.actual_duration_days = data.actual_duration_days;
    const response = await api.put(`/deals/${dealId}/renovation`, null, { params });
    return response.data;
  },

  /**
   * Update cost breakdown
   */
  updateBreakdown: async (dealId, breakdown) => {
    const response = await api.put(`/deals/${dealId}/renovation/breakdown`, breakdown);
    return response.data;
  },

  /**
   * Force recalculation of renovation impact
   */
  recalculate: async (dealId) => {
    const response = await api.post(`/deals/${dealId}/renovation/recalculate`);
    return response.data;
  },

  /**
   * Get all deals' renovation tracking (portfolio view)
   */
  getAllTracking: async () => {
    const response = await api.get('/renovation/all-tracking');
    return response.data;
  },
};

// ============== COMPARABLES ==============

export const comparablesApi = {
  /**
   * Get all comparables with filters
   */
  getAll: async (filters = {}) => {
    const response = await api.get('/comparables', { params: filters });
    return response.data;
  },

  /**
   * Get single comparable
   */
  getById: async (comparableId) => {
    const response = await api.get(`/comparables/${comparableId}`);
    return response.data;
  },

  /**
   * Create comparable
   */
  create: async (comparableData) => {
    const response = await api.post('/comparables', comparableData);
    return response.data;
  },

  /**
   * Update comparable
   */
  update: async (comparableId, comparableData) => {
    const response = await api.put(`/comparables/${comparableId}`, comparableData);
    return response.data;
  },

  /**
   * Delete comparable
   */
  delete: async (comparableId) => {
    const response = await api.delete(`/comparables/${comparableId}`);
    return response.data;
  },

  /**
   * Verify comparable
   */
  verify: async (comparableId, verifiedBy = 'analyst') => {
    const response = await api.post(`/comparables/${comparableId}/verify`, null, {
      params: { verified_by: verifiedBy }
    });
    return response.data;
  },

  /**
   * Exclude comparable from analysis
   */
  exclude: async (comparableId, reason = null) => {
    const response = await api.post(`/comparables/${comparableId}/exclude`, null, {
      params: { reason }
    });
    return response.data;
  },

  /**
   * Include comparable back in analysis
   */
  include: async (comparableId) => {
    const response = await api.post(`/comparables/${comparableId}/include`);
    return response.data;
  },

  /**
   * Assign comparable to a deal
   */
  assignToDeal: async (comparableId, dealId) => {
    const response = await api.post(`/comparables/${comparableId}/assign/${dealId}`);
    return response.data;
  },

  /**
   * Get comparable statistics
   */
  getStatistics: async (arrondissement = null) => {
    const response = await api.get('/comparables/statistics', {
      params: { arrondissement }
    });
    return response.data;
  },

  /**
   * Get comp analysis for a deal
   */
  getDealAnalysis: async (dealId, minRelevanceScore = 40.0, includeGlobalComps = true) => {
    const response = await api.get(`/deals/${dealId}/comp-analysis`, {
      params: { min_relevance_score: minRelevanceScore, include_global_comps: includeGlobalComps }
    });
    return response.data;
  },

  /**
   * Get top comps for a deal
   */
  getTopComps: async (dealId, limit = 5) => {
    const response = await api.get(`/deals/${dealId}/top-comps`, {
      params: { limit }
    });
    return response.data;
  },
};

// ============== PARTNERS ==============

export const partnersApi = {
  /**
   * Get all partners
   */
  getAll: async (activeOnly = false) => {
    const response = await api.get('/partners', { params: { active_only: activeOnly } });
    return response.data;
  },

  /**
   * Get single partner
   */
  getById: async (partnerId) => {
    const response = await api.get(`/partners/${partnerId}`);
    return response.data;
  },

  /**
   * Create partner
   */
  create: async (partnerData) => {
    const response = await api.post('/partners', partnerData);
    return response.data;
  },

  /**
   * Update partner
   */
  update: async (partnerId, partnerData) => {
    const response = await api.put(`/partners/${partnerId}`, partnerData);
    return response.data;
  },

  /**
   * Delete partner
   */
  delete: async (partnerId) => {
    const response = await api.delete(`/partners/${partnerId}`);
    return response.data;
  },

  // ===== SCORECARD =====

  /**
   * Get scorecard overview statistics
   */
  getScorecardOverview: async () => {
    const response = await api.get('/partners/scorecard/overview');
    return response.data;
  },

  /**
   * Get all partner scorecards
   */
  getAllScorecards: async () => {
    const response = await api.get('/partners/scorecard/all');
    return response.data;
  },

  /**
   * Get single partner scorecard
   */
  getScorecard: async (partnerId) => {
    const response = await api.get(`/partners/${partnerId}/scorecard`);
    return response.data;
  },

  /**
   * Get partners assigned to a deal
   */
  getDealPartners: async (dealId) => {
    const response = await api.get(`/deals/${dealId}/partners`);
    return response.data;
  },
};

// ===== AI INTELLIGENCE =====
export const intelligenceApi = {
  /**
   * Get complete AI intelligence analysis for a deal
   */
  getFullAnalysis: async (dealId) => {
    const response = await api.get(`/deals/${dealId}/intelligence`);
    return response.data;
  },

  /**
   * Get lightweight intelligence summary for deal cards
   */
  getSummary: async (dealId) => {
    const response = await api.get(`/deals/${dealId}/intelligence/summary`);
    return response.data;
  },
};

// ============== EXIT TRACKER ==============

export const exitTrackerApi = {
  /**
   * Get complete exit tracker analysis for a deal
   */
  getTracker: async (dealId) => {
    const response = await api.get(`/deals/${dealId}/exit-tracker`);
    return response.data;
  },

  /**
   * Update exit/listing information
   */
  updateListing: async (dealId, listingData) => {
    const params = new URLSearchParams();
    Object.entries(listingData).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        params.append(key, value);
      }
    });
    const response = await api.put(`/deals/${dealId}/exit-tracker?${params.toString()}`);
    return response.data;
  },

  /**
   * Record price reduction
   */
  recordPriceReduction: async (dealId, newPrice, reason = null) => {
    const params = new URLSearchParams({ new_price: newPrice });
    if (reason) params.append('reason', reason);
    const response = await api.post(`/deals/${dealId}/exit-tracker/price-reduction?${params.toString()}`);
    return response.data;
  },

  /**
   * Complete sale transaction
   */
  completeSale: async (dealId, saleData) => {
    const params = new URLSearchParams();
    Object.entries(saleData).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        params.append(key, value);
      }
    });
    const response = await api.post(`/deals/${dealId}/exit-tracker/complete-sale?${params.toString()}`);
    return response.data;
  },
};

// ============== OFFERS ==============

export const offersApi = {
  /**
   * Get all offers for a deal
   */
  getAll: async (dealId) => {
    const response = await api.get(`/deals/${dealId}/offers`);
    return response.data;
  },

  /**
   * Create new offer
   */
  create: async (dealId, offerData) => {
    const response = await api.post(`/deals/${dealId}/offers`, offerData);
    return response.data;
  },

  /**
   * Update offer
   */
  update: async (dealId, offerId, offerData) => {
    const response = await api.put(`/deals/${dealId}/offers/${offerId}`, offerData);
    return response.data;
  },

  /**
   * Delete offer
   */
  delete: async (dealId, offerId) => {
    const response = await api.delete(`/deals/${dealId}/offers/${offerId}`);
    return response.data;
  },

  /**
   * Analyze single offer
   */
  analyze: async (dealId, offerId) => {
    const response = await api.post(`/deals/${dealId}/offers/${offerId}/analyze`);
    return response.data;
  },

  /**
   * Accept offer
   */
  accept: async (dealId, offerId) => {
    const response = await api.post(`/deals/${dealId}/offers/${offerId}/accept`);
    return response.data;
  },

  /**
   * Reject offer
   */
  reject: async (dealId, offerId, reason = null) => {
    const params = reason ? `?reason=${encodeURIComponent(reason)}` : '';
    const response = await api.post(`/deals/${dealId}/offers/${offerId}/reject${params}`);
    return response.data;
  },

  /**
   * Counter offer
   */
  counter: async (dealId, offerId, counterPrice) => {
    const response = await api.post(`/deals/${dealId}/offers/${offerId}/counter?counter_price=${counterPrice}`);
    return response.data;
  },
};

// ============== SETTINGS ==============

export const settingsApi = {
  /**
   * Get default assumptions
   */
  getDefaults: async () => {
    const response = await api.get('/settings/defaults');
    return response.data;
  },

  /**
   * Update default assumptions
   */
  updateDefaults: async (defaults) => {
    const response = await api.put('/settings/defaults', defaults);
    return response.data;
  },
};

// ============== DATA IMPORT ==============

export const importApi = {
  /**
   * Get all configured data sources
   */
  getSources: async () => {
    const response = await api.get('/import/sources');
    return response.data;
  },

  /**
   * Get single data source
   */
  getSource: async (sourceId) => {
    const response = await api.get(`/import/source/${sourceId}`);
    return response.data;
  },

  /**
   * Get review queue with filters
   */
  getQueue: async (params = {}) => {
    const response = await api.get('/import/queue', { params });
    return response.data;
  },

  /**
   * Get single queue record
   */
  getQueueRecord: async (recordId) => {
    const response = await api.get(`/import/queue/${recordId}`);
    return response.data;
  },

  /**
   * Preview CSV import
   */
  previewCsv: async (csvContent, delimiter = ',') => {
    const response = await api.post('/import/csv/preview', null, {
      params: { csv_content: csvContent, delimiter }
    });
    return response.data;
  },

  /**
   * Import from CSV
   */
  importCsv: async (csvContent, mapping, sourceId = 'csv_upload', observationType = 'asking', delimiter = ',') => {
    const response = await api.post('/import/csv', {
      csv_content: csvContent,
      mapping,
      source_id: sourceId,
      observation_type: observationType,
      delimiter
    });
    return response.data;
  },

  /**
   * Approve a queue record
   */
  approveRecord: async (recordId) => {
    const response = await api.post(`/import/queue/${recordId}/approve`);
    return response.data;
  },

  /**
   * Reject a queue record
   */
  rejectRecord: async (recordId, reason = null) => {
    const params = reason ? { reason } : {};
    const response = await api.post(`/import/queue/${recordId}/reject`, null, { params });
    return response.data;
  },

  /**
   * Update a queue record
   */
  updateRecord: async (recordId, updates) => {
    const response = await api.put(`/import/queue/${recordId}`, updates);
    return response.data;
  },

  /**
   * Convert approved record to comparable
   */
  convertToComparable: async (recordId) => {
    const response = await api.post(`/import/queue/${recordId}/convert`);
    return response.data;
  },

  /**
   * Get import batches
   */
  getBatches: async (params = {}) => {
    const response = await api.get('/import/batches', { params });
    return response.data;
  },

  /**
   * Get single batch
   */
  getBatch: async (batchId) => {
    const response = await api.get(`/import/batches/${batchId}`);
    return response.data;
  },

  /**
   * Delete a queue record
   */
  deleteRecord: async (recordId) => {
    const response = await api.delete(`/import/queue/${recordId}`);
    return response.data;
  },

  /**
   * Bulk approve records
   */
  bulkApprove: async (recordIds) => {
    const response = await api.post('/import/queue/bulk-approve', recordIds);
    return response.data;
  },

  /**
   * Bulk reject records
   */
  bulkReject: async (recordIds, reason = null) => {
    const response = await api.post('/import/queue/bulk-reject', { record_ids: recordIds, reason });
    return response.data;
  },
};

// ============== HEALTH ==============

export const healthApi = {
  /**
   * Check API health
   */
  check: async () => {
    const response = await api.get('/health');
    return response.data;
  },
};

// ============== SEED (Development) ==============

export const seedApi = {
  /**
   * Seed database with sample data
   */
  seed: async () => {
    const response = await api.post('/seed');
    return response.data;
  },
};

export default api;
